import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import { formatTourId } from '../utils/tour';

const STATUS_OPTIONS = ['', 'submitted', 'acknowledged', 'resolved', 'escalated'];

export default function Reports() {
  const { user, API } = useAuth();

  const [view, setView] = useState('summary');     // 'summary' | 'issues' | 'trends'
  const [rows, setRows] = useState([]);
  const [trends, setTrends] = useState(null);      // { months, overall, byFacility }
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState('');
  const [error, setError] = useState('');

  // Filters
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [facilityId, setFacilityId] = useState('');
  const [status, setStatus] = useState('');
  const [facilities, setFacilities] = useState([]);

  // Building admins are server-scoped to their facility — no picker needed.
  const showFacilityFilter = user?.role !== 'building_admin';

  useEffect(() => {
    if (showFacilityFilter) {
      API.get('/admin/facilities').then(r => setFacilities(r.data)).catch(() => {});
    }
  }, []);

  const buildQuery = (fmt) => {
    const p = new URLSearchParams();
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    if (facilityId) p.set('facilityId', facilityId);
    if (status) p.set('status', status);
    p.set('format', fmt);
    return p.toString();
  };

  const endpoint = view === 'summary' ? '/reports/tours-summary'
    : view === 'issues' ? '/reports/issues-detail'
    : view === 'deferred' ? '/reports/deferred-issues'
    : '/reports/trends';

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await API.get(`${endpoint}?${buildQuery('json')}`);
      if (view === 'trends') {
        setTrends(r.data);
        setRows(r.data.byFacility || []);
      } else {
        setRows(r.data);
        setTrends(null);
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setRows([]);
      setTrends(null);
    } finally {
      setLoading(false);
    }
  }, [view, from, to, facilityId, status]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  // Authenticated blob download — plain links can't carry the bearer token.
  const download = async (fmt) => {
    setExporting(fmt);
    try {
      const r = await API.get(`${endpoint}?${buildQuery(fmt)}`, { responseType: 'blob' });
      const disposition = r.headers['content-disposition'] || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `pillar-report.${fmt}`;
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export failed: ' + (e.response?.data?.error || e.message));
    } finally {
      setExporting('');
    }
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h2>Reports</h2>
          <p>{rows.length} {rows.length === 1 ? 'row' : 'rows'} matching current filters</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => download('csv')} disabled={!!exporting || loading || rows.length === 0}>
            {exporting === 'csv' ? 'Exporting...' : '⬇ CSV'}
          </button>
          <button className="btn btn-primary" onClick={() => download('xlsx')} disabled={!!exporting || loading || rows.length === 0}>
            {exporting === 'xlsx' ? 'Exporting...' : '⬇ Excel'}
          </button>
        </div>
      </div>

      <div className="page-body">
        {/* View tabs */}
        <div className="tabs">
          <button className={`tab-btn ${view === 'summary' ? 'active' : ''}`} onClick={() => setView('summary')}>Tour Summary</button>
          <button className={`tab-btn ${view === 'issues' ? 'active' : ''}`} onClick={() => setView('issues')}>Issue Detail</button>
          <button className={`tab-btn ${view === 'trends' ? 'active' : ''}`} onClick={() => setView('trends')}>Compliance Trends</button>
          <button className={`tab-btn ${view === 'deferred' ? 'active' : ''}`} onClick={() => setView('deferred')}>Deferred Issues</button>
        </div>

        {/* Filters */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: showFacilityFilter ? '1fr 1fr 1.4fr 1fr' : '1fr 1fr 1fr', gap: 12 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">From</label>
              <input type="date" className="form-control" value={from} onChange={e => setFrom(e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">To</label>
              <input type="date" className="form-control" value={to} onChange={e => setTo(e.target.value)} />
            </div>
            {showFacilityFilter && (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Facility</label>
                <select className="form-control" value={facilityId} onChange={e => setFacilityId(e.target.value)}>
                  <option value="">All facilities</option>
                  {facilities.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </div>
            )}
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Status</label>
              <select className="form-control" value={status} onChange={e => setStatus(e.target.value)}>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s === '' ? 'All statuses' : s}</option>)}
              </select>
            </div>
          </div>
        </div>

        {error && <div className="alert-banner alert-danger">{error}</div>}

        {/* Preview */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : rows.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📊</div>
              <h3>No data</h3>
              <p>No {view === 'summary' ? 'tours' : 'issues'} match the current filters.</p>
            </div>
          ) : view === 'deferred' ? (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Facility</th><th>Question</th><th>Reason</th><th>Deferred Since</th><th>Target Date</th><th>By</th></tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600, fontSize: 13 }}>{r.facility_name}<div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{r.region_name}</div></td>
                      <td style={{ fontSize: 13, maxWidth: 300 }}>{r.question_text}<div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{r.category_name}</div></td>
                      <td style={{ fontSize: 13, maxWidth: 260 }}>{r.deferred_reason}</td>
                      <td style={{ fontSize: 13 }}>{format(new Date(r.deferred_at), 'MMM d, yyyy')}<div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{r.weeks_outstanding} wk{parseInt(r.weeks_outstanding) === 1 ? '' : 's'}</div></td>
                      <td style={{ fontSize: 13, fontWeight: r.overdue ? 700 : 400, color: r.overdue ? 'var(--red)' : 'inherit' }}>
                        {r.deferred_until ? format(new Date(r.deferred_until), 'MMM d, yyyy') : '—'}
                        {r.overdue && <div style={{ fontSize: 11 }}>OVERDUE</div>}
                      </td>
                      <td style={{ fontSize: 12 }}>{r.deferred_by_name || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : view === 'trends' ? (
            trends && trends.overall.length > 0 ? (
              <div style={{ padding: 20 }}>
                {/* Overall monthly average — inline SVG line chart */}
                {(() => {
                  const W = 720, H = 220, PL = 44, PB = 28, PT = 14, PR = 12;
                  const data = trends.overall;
                  const minY = Math.max(0, Math.floor(Math.min(...data.map(d => d.avg_score)) / 10) * 10 - 10);
                  const maxY = 100;
                  const x = i => PL + (data.length === 1 ? (W - PL - PR) / 2 : (i * (W - PL - PR)) / (data.length - 1));
                  const y = v => PT + (H - PT - PB) * (1 - (v - minY) / (maxY - minY));
                  const points = data.map((d, i) => `${x(i)},${y(d.avg_score)}`).join(' ');
                  const gridLines = [];
                  for (let v = minY; v <= maxY; v += 10) gridLines.push(v);
                  return (
                    <div style={{ overflowX: 'auto' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-heading)', marginBottom: 8 }}>
                        Average Compliance Score by Month {facilityId ? '(selected facility)' : '(all facilities in your scope)'}
                      </div>
                      <svg width={W} height={H} style={{ maxWidth: '100%' }}>
                        {gridLines.map(v => (
                          <g key={v}>
                            <line x1={PL} y1={y(v)} x2={W - PR} y2={y(v)} stroke="var(--border)" strokeWidth="1" />
                            <text x={PL - 6} y={y(v) + 3} textAnchor="end" fontSize="10" fill="var(--text-secondary)">{v}%</text>
                          </g>
                        ))}
                        <polyline points={points} fill="none" stroke="#0D9488" strokeWidth="2.5" />
                        {data.map((d, i) => (
                          <g key={d.month}>
                            <circle cx={x(i)} cy={y(d.avg_score)} r="4" fill="#0D9488" />
                            <text x={x(i)} y={y(d.avg_score) - 9} textAnchor="middle" fontSize="10" fontWeight="700" fill="#0D9488">{d.avg_score}</text>
                            <text x={x(i)} y={H - 8} textAnchor="middle" fontSize="10" fill="var(--text-secondary)">{d.month}</text>
                          </g>
                        ))}
                      </svg>
                    </div>
                  );
                })()}

                {/* Per-facility monthly table */}
                <div className="table-wrap" style={{ marginTop: 20 }}>
                  <table>
                    <thead>
                      <tr><th>Month</th><th>Facility</th><th>Avg Compliance</th><th>Tours</th></tr>
                    </thead>
                    <tbody>
                      {trends.byFacility.map((r, i) => (
                        <tr key={i}>
                          <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 13 }}>{r.month}</td>
                          <td style={{ fontWeight: 600, fontSize: 13 }}>{r.facility_name}</td>
                          <td style={{ fontWeight: 700, color: parseFloat(r.avg_score) >= 90 ? 'var(--green)' : parseFloat(r.avg_score) >= 75 ? 'var(--amber)' : 'var(--red)' }}>{r.avg_score}%</td>
                          <td style={{ fontSize: 13 }}>{r.tour_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">📈</div>
                <h3>No trend data</h3>
                <p>No submitted tours match the current filters.</p>
              </div>
            )
          ) : view === 'summary' ? (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Tour ID</th><th>Facility</th><th>Region</th><th>Conducted By</th>
                    <th>Submitted</th><th>Status</th><th>Score</th><th>Issues</th><th>Sign-offs</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i}>
                      <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 13 }}>{formatTourId(r.tour_number)}</td>
                      <td style={{ fontWeight: 600 }}>{r.facility_name}</td>
                      <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{r.region_name || '—'}</td>
                      <td style={{ fontSize: 13 }}>{r.conducted_by}</td>
                      <td style={{ fontSize: 13 }}>{format(new Date(r.submitted_at), 'MMM d, yyyy')}</td>
                      <td><span className={`badge badge-${r.status}`}>{r.status}</span></td>
                      <td style={{ fontWeight: 700 }}>{parseFloat(r.compliance_score).toFixed(1)}%</td>
                      <td>
                        {parseInt(r.open_issues) > 0
                          ? <span style={{ color: 'var(--red)', fontWeight: 600 }}>{r.open_issues} open</span>
                          : <span style={{ color: 'var(--green)' }}>{r.total_issues} / clear</span>}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {r.maint_signoff_by && <div>🔧 {r.maint_signoff_by}</div>}
                        {r.env_signoff_by && <div>🧹 {r.env_signoff_by}</div>}
                        {!r.maint_signoff_by && !r.env_signoff_by && <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Tour ID</th><th>Facility</th><th>Category</th><th>Question</th>
                    <th>Status</th><th>Resolution</th><th>Maint.</th><th>Environ.</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i}>
                      <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 13 }}>{formatTourId(r.tour_number)}</td>
                      <td style={{ fontWeight: 600, fontSize: 13 }}>{r.facility_name}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{r.category_name}</td>
                      <td style={{ fontSize: 13, maxWidth: 320 }}>{r.question_text}</td>
                      <td><span className={`badge badge-${(r.issue_status || '').replace('_', '-')}`}>{(r.issue_status || '').replace('_', ' ')}</span></td>
                      <td style={{ fontSize: 12 }}>
                        {r.resolved_by
                          ? <>{r.resolved_by}<div style={{ color: 'var(--text-secondary)' }}>{format(new Date(r.resolved_at), 'MMM d')}</div></>
                          : <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {r.maint_status
                          ? <span style={{ color: r.maint_status === 'complete' ? 'var(--green)' : 'var(--amber)', fontWeight: 600, textTransform: 'capitalize' }}>{r.maint_status.replace('_', ' ')}</span>
                          : <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {r.env_status
                          ? <span style={{ color: r.env_status === 'complete' ? 'var(--green)' : 'var(--amber)', fontWeight: 600, textTransform: 'capitalize' }}>{r.env_status.replace('_', ' ')}</span>
                          : <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
